import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldAlert, HelpCircle, Loader2, ScanSearch, Upload, AlertTriangle } from "lucide-react";

const VERDICTS = {
  stock: { icon: ShieldCheck, color: "text-signal-success", ring: "border-signal-success/40 bg-signal-success/5", label: "Fichier d'origine (stock)" },
  tuned_known: { icon: ShieldAlert, color: "text-signal-error", ring: "border-signal-error/40 bg-signal-error/5", label: "Déjà modifié (solution connue)" },
  likely_tuned: { icon: AlertTriangle, color: "text-signal-processing", ring: "border-signal-processing/40 bg-signal-processing/5", label: "Probablement déjà modifié" },
  unknown: { icon: HelpCircle, color: "text-zinc-400", ring: "border-white/15 bg-white/5", label: "Indéterminé" },
};

export const TuningStateChecker = () => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (f) => {
    if (!f) return;
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/analyze/tuning-state", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse impossible");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const v = res ? (VERDICTS[res.verdict] || VERDICTS.unknown) : null;
  const Icon = v?.icon;

  return (
    <div className="glass rounded-md p-5" data-testid="tuning-checker">
      <div className="flex items-center gap-2 mb-1">
        <ScanSearch className="w-4 h-4 text-aurora-cyan" />
        <h3 className="font-display text-base font-semibold uppercase tracking-tight">Vérificateur de fichier · déjà modifié ?</h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">Déposez le fichier envoyé par le client : le système compare aux originaux d'usine connus et vous dit s'il est <b className="text-signal-success">d'origine</b> ou <b className="text-signal-error">déjà tuné</b> (avant d'appliquer un nouveau Stage).</p>

      <input ref={fileRef} type="file" className="hidden" data-testid="checker-file-input" onChange={(e) => run(e.target.files?.[0])} />
      <button data-testid="checker-run-btn" onClick={() => fileRef.current?.click()} disabled={busy}
        className="flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Analyser un fichier
      </button>

      {res && v && (
        <div data-testid="checker-result" className={`mt-4 rounded-md border p-4 ${v.ring}`}>
          <div className="flex items-center gap-2.5 mb-2">
            <Icon className={`w-5 h-5 ${v.color}`} />
            <span className={`font-display text-sm uppercase tracking-tight ${v.color}`}>{v.label}</span>
            {res.confidence && <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-zinc-500">fiabilité : {res.confidence}</span>}
          </div>
          <p className="font-mono text-[11px] text-zinc-300 mb-2">{res.message}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-zinc-500">
            <span>{res.filename} · {res.size}o</span>
            {res.brand && <span>Véhicule : <span className="text-zinc-300">{res.brand} {res.model || ""}</span></span>}
            {res.ecu && <span>ECU : <span className="text-aurora-cyan">{res.ecu}</span></span>}
            {res.changed_bytes != null && <span>Écart : <span className="text-signal-processing">{res.changed_bytes} octets ({Math.round((res.ratio || 0) * 100)}%)</span></span>}
            {(res.matched_services?.length > 0) && <span>Tune détecté : <span className="text-signal-error uppercase">{res.matched_services.join(", ")}</span>{res.matched_ref ? ` (${res.matched_ref})` : ""}</span>}
            {res.services?.length > 0 && <span>Solutions : <span className="text-signal-error uppercase">{res.services.join(", ")}</span></span>}
            {res.software?.length > 0 && <span>Soft : <span className="text-zinc-300">{res.software.slice(0, 3).join(" · ")}</span></span>}
          </div>
        </div>
      )}
    </div>
  );
};
